import { EXPORT_ENDPOINT } from 'api/constants';

import type {
  ExportPayload,
  ExportResult,
  PromptExportResult,
} from './export.api.types';

function parseFilenameFromDisposition(header: string): string {
  const parts = header.split('filename=');
  if (parts.length < 2) return 'export';
  const raw = parts[1].trim();
  if (raw.startsWith('"') && raw.endsWith('"')) {
    return raw.slice(1, -1);
  }
  return raw.split(';')[0].trim() || 'export';
}

export async function exportContent(
  payload: ExportPayload,
): Promise<ExportResult> {
  const response = await fetch(EXPORT_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    try {
      const error = await response.json();
      throw error;
    } catch {
      throw new Error(`Export failed with status ${response.status}`);
    }
  }

  const blob = await response.blob();
  const disposition = response.headers.get('content-disposition') ?? '';
  const filename = parseFilenameFromDisposition(disposition);

  return { blob, filename };
}

export async function generatePrompt(
  threadId: string,
  signal?: AbortSignal,
): Promise<PromptExportResult> {
  const response = await fetch(EXPORT_ENDPOINT, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ exportFormat: 'prompt', threadId }),
    signal,
  });

  if (!response.ok) {
    try {
      const error = await response.json();
      throw error;
    } catch {
      throw new Error(
        `Prompt generation failed with status ${response.status}`,
      );
    }
  }

  const data = (await response.json()) as Record<string, unknown>;
  if (typeof data.prompt !== 'string') {
    throw new Error('Unexpected response shape from prompt export');
  }
  return { prompt: data.prompt };
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
